import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import {
  getLeadhoopAccounts,
  getLeadhoopLead,
  type LeadhoopLeadDetail,
} from "@/api/leadhoop";
import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { Spinner } from "@/components/Spinner";
import { fmtDate, fmtDateTime, fmtMoney } from "@/lib/format";

function Section({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold text-slate-800"
      >
        <span>{title}</span>
        <span className="text-slate-400">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="border-t border-slate-200 px-4 py-3">{children}</div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex flex-col">
      <span className="text-xs uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <span className="text-sm text-slate-800">{value ?? "—"}</span>
    </div>
  );
}

export function LeadhoopLeadDetailPage() {
  const { accountSlug = "", recordId = "" } = useParams();
  const rid = Number(recordId);

  const { data: accounts } = useQuery({
    queryKey: ["leadhoop", "accounts"],
    queryFn: getLeadhoopAccounts,
  });

  const {
    data: lead,
    isLoading,
    error,
  } = useQuery<LeadhoopLeadDetail>({
    queryKey: ["leadhoop", "lead", accountSlug, rid],
    queryFn: () => getLeadhoopLead(accountSlug, rid),
    enabled: Boolean(accountSlug) && Number.isFinite(rid),
  });

  const [copied, setCopied] = useState(false);

  async function copyRaw() {
    if (!lead) return;
    await navigator.clipboard.writeText(JSON.stringify(lead.raw, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  const accountBase = accounts?.find(
    (a) => a.slug === accountSlug,
  )?.base_url;
  const leadhoopUrl =
    accountBase && Number.isFinite(rid)
      ? `${accountBase}/admin/lead_requests/${rid}`
      : null;

  return (
    <div className="space-y-4">
      <PageHeader
        title={`Lead ${accountSlug}/${recordId}`}
        subtitle={lead?.status ?? undefined}
        actions={
          <div className="flex gap-2">
            <Link to="/leadhoop/leads" className="btn-secondary">
              Back to list
            </Link>
            {leadhoopUrl && (
              <a
                href={leadhoopUrl}
                target="_blank"
                rel="noreferrer"
                className="btn-secondary"
              >
                View on LeadHoop ↗
              </a>
            )}
          </div>
        }
      />

      {error && <ErrorBanner error={error} />}
      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Spinner /> Loading lead…
        </div>
      )}

      {lead && (
        <div className="space-y-3">
          <Section title="Identifiers">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Field label="Record ID" value={lead.record_id} />
              <Field label="Account" value={lead.account_slug} />
              <Field
                label="Publisher session"
                value={`${lead.publisher_session_name ?? ""} (${lead.publisher_session_id})`}
              />
              <Field label="Consumer ID" value={lead.consumer_id ?? "—"} />
            </div>
          </Section>

          <Section title="Dates & status">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Field label="Status" value={lead.status} />
              <Field
                label="Create date"
                value={fmtDateTime(lead.create_date)}
              />
              <Field
                label="Status update"
                value={fmtDateTime(lead.status_update_date)}
              />
              <Field label="Test" value={lead.test ? "Yes" : "No"} />
            </div>
          </Section>

          <Section title="Campaign & offer">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Field label="Division" value={lead.division} />
              <Field label="Offer" value={lead.offer} />
              <Field label="Offer ID" value={lead.offer_id} />
              <Field label="External name" value={lead.external_name} />
              <Field label="Publisher" value={lead.publisher} />
              <Field label="Publisher ID" value={lead.publisher_id} />
              <Field label="Media type" value={lead.media_type} />
              <Field label="Offer type" value={lead.offer_type} />
              <Field label="Campaign type" value={lead.campaign_type} />
              <Field label="Revenue" value={fmtMoney(lead.revenue)} />
              <Field label="Disposition" value={lead.disposition} />
            </div>
          </Section>

          <Section title="Contact">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Field label="First name" value={lead.first_name} />
              <Field label="Last name" value={lead.last_name} />
              <Field label="Email" value={lead.email} />
              <Field label="Phone" value={lead.phone_1} />
              <Field label="Address" value={lead.address} />
              <Field label="Address 2" value={lead.address_2} />
              <Field label="City" value={lead.city} />
              <Field label="State" value={lead.state} />
              <Field label="Zip" value={lead.zip} />
            </div>
          </Section>

          <Section title="Demographics">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Field label="Gender" value={lead.gender} />
              <Field label="Age" value={lead.age} />
              <Field label="DOB" value={fmtDate(lead.dob)} />
              <Field label="IP" value={lead.ip} />
            </div>
          </Section>

          <Section title="Tracking / UTM">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Field label="UTM source" value={lead.utm_source} />
              <Field label="UTM medium" value={lead.utm_medium} />
              <Field label="UTM campaign" value={lead.utm_campaign} />
              <Field label="UTM term" value={lead.utm_term} />
              <Field label="UTM content" value={lead.utm_content} />
              <Field label="Signup URL" value={lead.signup_url} />
              <Field label="Consent URL" value={lead.consent_url} />
            </div>
          </Section>

          <Section title="Education" defaultOpen={false}>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Field label="Campus" value={lead.campus} />
              <Field label="Campus type" value={lead.campus_type} />
              <Field label="Program" value={lead.program} />
              <Field label="Degree" value={lead.degree} />
              <Field label="Education level" value={lead.education_level} />
              <Field label="Grad year" value={lead.grad_year} />
            </div>
          </Section>

          <Section title="Raw JSON" defaultOpen={false}>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={copyRaw}
                className="btn-secondary mb-2"
              >
                {copied ? "Copied!" : "Copy JSON"}
              </button>
            </div>
            <pre className="max-h-96 overflow-auto rounded-md bg-slate-900 p-3 text-xs text-slate-100">
              {JSON.stringify(lead.raw, null, 2)}
            </pre>
          </Section>
        </div>
      )}
    </div>
  );
}
